import { useEffect, useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getCRMContactFollowUps,
  createCRMContactFollowUp,
  pauseCRMContactFollowUp,
  resumeCRMContactFollowUp,
  stopCRMContactFollowUp,
  deleteCRMContactFollowUp,
  rescheduleCRMContactFollowUp,
} from '../api';
import { PlusIcon } from '../../components/Icons';
import { Button, ConfirmDeleteModal, SearchInput } from '@/components';
import { V2Table } from '@/common/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';
import { TableChip } from '../../components/PremiumDesign';
import trashIcon from '../../assets/trashIcon.png';
import eyeIcon from '../../assets/eye.png';
import pauseCircleIcon from '../../assets/leads/pauseCircle.png';
import rescheduleIcon from '../../assets/leads/reschedule.png';
import playIcon from '../../assets/leads/play.png';
import pauseButtonIcon from '../../assets/leads/pauseButton.png';
import { CRMFollowUpForm } from './CRMFollowUpForm';
import { getContactChannelCapabilities } from '../utils/contactCapabilities';
import { CRMFollowUpDetailModal } from './CRMFollowUpDetailModal';
import { CRMFollowUpChannelModal } from './CRMFollowUpChannelModal';
import { RescheduleModal } from './RescheduleModal';
import { StopFollowUpConfirmModal } from '../../components/StopFollowUpConfirmModal';
import { ActionConfirmModal } from '../../components/ActionConfirmModal';
import { parseApiError } from '@/common/utils';
import { getFollowUpStatusChipStyle } from '../utils/tasksAndFollowups';
import { pageWindow } from '../utils/pagedList';

const PAGE_SIZE = 10;

function isValidDate(dateString) {
  if (!dateString) return false;
  const d = new Date(dateString);
  return !isNaN(d.getTime());
}

function formatDate(dateString) {
  if (!isValidDate(dateString)) return '-';
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function CRMFollowUpsSection({
  companyId,
  contactId,
  contact,
  readOnly = false,
  onCreatingChange,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const channels = useMessagingChannels(companyId);
  const [isCreating, setIsCreatingState] = useState(false);
  const setIsCreating = (value) => {
    setIsCreatingState(value);
    onCreatingChange?.(value);
  };
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [initialChannel, setInitialChannel] = useState('whatsapp');
  const [viewingFollowUpId, setViewingFollowUpId] = useState(null);
  const [actionPopup, setActionPopup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const handleStartAddFollowUp = () => {
    if (!channels.isReady) {
      setShowChannelModal(true);
      return;
    }
    const { hasEmail, hasPhone } = getContactChannelCapabilities(contact);
    const emailOk = channels.email && (hasEmail || (!hasEmail && !hasPhone));
    const whatsappOk =
      channels.whatsapp && (hasPhone || (!hasEmail && !hasPhone));
    if (emailOk && whatsappOk) {
      setShowChannelModal(true);
      return;
    }
    if (emailOk) {
      setInitialChannel('email');
      setIsCreating(true);
      return;
    }
    if (whatsappOk) {
      setInitialChannel('whatsapp');
      setIsCreating(true);
      return;
    }
    setShowChannelModal(true);
  };

  const handleSelectChannel = (selectedChannel) => {
    if (
      !channels.isReady ||
      (selectedChannel === 'email' && !channels.email) ||
      (selectedChannel === 'whatsapp' && !channels.whatsapp)
    ) {
      return;
    }
    setShowChannelModal(false);
    setInitialChannel(selectedChannel);
    setIsCreating(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: followUpsData,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'crm-contact-follow-ups',
      companyId,
      contactId,
      page,
      debouncedSearch,
    ],
    queryFn: () =>
      getCRMContactFollowUps({
        companyId,
        contactId,
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      }),
    enabled: !!companyId && !!contactId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && followUpsData !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  const { rows: followUps, totalCount } = pageWindow(
    followUpsData,
    page,
    PAGE_SIZE,
  );

  const invalidateFollowUps = () => {
    queryClient.invalidateQueries({
      queryKey: ['crm-contact-follow-ups', companyId, contactId],
    });
    if (viewingFollowUpId) {
      queryClient.invalidateQueries({
        queryKey: [
          'crm-contact-follow-up-detail',
          companyId,
          contactId,
          viewingFollowUpId,
        ],
      });
    }
  };

  const { mutate: handleAddFollowUp, isPending: isAdding } = useMutation({
    mutationFn: (vars) => {
      if (!canCreate) throw new Error('Permission denied.');
      const channel = vars?.payload?.channel || vars?.channel;
      if (
        !channels.isReady ||
        (channel === 'email' && !channels.email) ||
        (channel === 'whatsapp' && !channels.whatsapp)
      ) {
        throw new Error('That channel is not connected.');
      }
      return createCRMContactFollowUp(vars);
    },
    onSuccess: () => {
      toast.success('Follow-up added successfully!');
      setIsCreating(false);
      setPage(1);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add follow-up.');
    },
  });

  const { mutate: handlePauseFollowUp, isPending: isPausing } = useMutation({
    mutationFn: (vars) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return pauseCRMContactFollowUp(vars);
    },
    onSuccess: () => {
      toast.success('Follow-up paused successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to pause follow-up.');
    },
  });

  const { mutate: handleResumeFollowUp, isPending: isResuming } = useMutation({
    mutationFn: (vars) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return resumeCRMContactFollowUp(vars);
    },
    onSuccess: () => {
      toast.success('Follow-up resumed successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to resume follow-up.');
    },
  });

  const { mutate: handleStopFollowUp, isPending: isStopping } = useMutation({
    mutationFn: (vars) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return stopCRMContactFollowUp(vars);
    },
    onSuccess: () => {
      toast.success('Follow-up stopped successfully!');
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to stop follow-up.');
    },
  });

  const { mutate: handleRescheduleFollowUp, isPending: isRescheduling } =
    useMutation({
      mutationFn: (vars) => {
        if (!canUpdate) throw new Error('Permission denied.');
        return rescheduleCRMContactFollowUp(vars);
      },
      onSuccess: () => {
        toast.success('Follow-up rescheduled successfully!');
        setActionPopup(null);
        invalidateFollowUps();
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to reschedule follow-up.');
      },
    });

  const { mutate: handleDeleteFollowUp, isPending: isDeleting } = useMutation({
    mutationFn: (vars) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteCRMContactFollowUp(vars);
    },
    onSuccess: () => {
      toast.success('Follow-up deleted successfully!');
      if (viewingFollowUpId === actionPopup?.id) {
        setViewingFollowUpId(null);
      }
      setActionPopup(null);
      invalidateFollowUps();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete follow-up.');
    },
  });

  const submitAddFollowUp = (payload) => {
    handleAddFollowUp({ companyId, contactId, payload });
  };

  if (isCreating) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto custom-scrollbar">
        <CRMFollowUpForm
          companyId={companyId}
          contact={contact}
          initialChannel={initialChannel}
          onSubmit={submitAddFollowUp}
          isPending={isAdding}
          onCancel={() => setIsCreating(false)}
        />
      </div>
    );
  }

  const followUpTitle = (followUp) =>
    followUp.channel === 'email'
      ? followUp.email_subject ||
        followUp.standard_message_body ||
        'Email Follow-up'
      : followUp.message_type === 'template'
        ? followUp.template_name || 'Template Follow-up'
        : 'Custom Message';

  const columns = [
    {
      id: 'template',
      label: 'Template Name',
      defaultWidth: 200,
      minWidth: 140,
      cell: (followUp) => {
        const label = followUpTitle(followUp);
        return (
          <span className="truncate block text-sm" title={label}>
            {label}
          </span>
        );
      },
    },
    {
      id: 'templateId',
      label: 'Template ID',
      defaultWidth: 140,
      minWidth: 100,
      cell: (followUp) => (
        <span className="truncate block text-sm">
          {followUp.template_id || '-'}
        </span>
      ),
    },
    {
      id: 'type',
      label: 'Follow-Up Type',
      defaultWidth: 140,
      minWidth: 110,
      cell: (followUp) => (
        <span className="truncate block text-sm capitalize">
          {followUp.follow_up_type || '-'}
        </span>
      ),
    },
    {
      id: 'message',
      label: 'Message Type',
      defaultWidth: 140,
      minWidth: 110,
      cell: (followUp) => (
        <span className="truncate block text-sm capitalize">
          {followUp.message_type || '-'}
        </span>
      ),
    },
    {
      id: 'attempts',
      label: 'Attempts',
      defaultWidth: 110,
      minWidth: 90,
      cell: (followUp) => (
        <span className="truncate block text-sm">
          {followUp.completed_attempts || 0}/
          {followUp.max_attempts > 0 ? followUp.max_attempts : '∞'}
        </span>
      ),
    },
    {
      id: 'next',
      label: 'Next Execution',
      defaultWidth: 170,
      minWidth: 140,
      cell: (followUp) => (
        <span className="truncate block text-sm">
          {followUp.follow_up_type === 'interval'
            ? formatDate(followUp.next_execution_time)
            : '-'}
        </span>
      ),
    },
    {
      id: 'scheduled',
      label: 'Scheduled For',
      defaultWidth: 170,
      minWidth: 140,
      cell: (followUp) => (
        <span className="truncate block text-sm">
          {followUp.follow_up_type === 'scheduled'
            ? formatDate(followUp.scheduled_date_time)
            : '-'}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 130,
      minWidth: 100,
      cell: (followUp) => (
        <TableChip {...getFollowUpStatusChipStyle(followUp.status)}>
          {followUp.status || '-'}
        </TableChip>
      ),
    },
    {
      id: 'actions',
      label: 'Action',
      defaultWidth: 150,
      minWidth: 120,
      align: 'center',
      stopPropagation: true,
      cell: (followUp) => {
        const status = String(followUp?.status || '').toLowerCase();
        return (
          <div className="flex items-center justify-end gap-1">
            {!readOnly && status === 'scheduled' && (
              <button
                type="button"
                onClick={() =>
                  setActionPopup({ type: 'stop', id: followUp.id })
                }
                disabled={isStopping}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none disabled:opacity-50"
                title="Stop Follow-up"
              >
                <img
                  src={pauseCircleIcon}
                  alt="Stop"
                  className="w-4 h-4 object-contain"
                />
              </button>
            )}
            {!readOnly && (status === 'stopped' || status === 'failed') && (
              <button
                type="button"
                onClick={() =>
                  setActionPopup({ type: 'reschedule', id: followUp.id })
                }
                disabled={isRescheduling}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none disabled:opacity-50"
                title="Reschedule Follow-up"
              >
                <img
                  src={rescheduleIcon}
                  alt="Reschedule"
                  className="w-4 h-4 object-contain"
                />
              </button>
            )}
            {!readOnly && status === 'paused' && (
              <button
                type="button"
                onClick={() =>
                  setActionPopup({ type: 'resume', id: followUp.id })
                }
                disabled={isResuming}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none disabled:opacity-50"
                title="Start Follow-up"
              >
                <img
                  src={playIcon}
                  alt="Start"
                  className="w-4 h-4 object-contain"
                />
              </button>
            )}
            {!readOnly && (status === 'active' || status === 'running') && (
              <button
                type="button"
                onClick={() =>
                  setActionPopup({ type: 'pause', id: followUp.id })
                }
                disabled={isPausing}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none disabled:opacity-50"
                title="Pause Follow-up"
              >
                <img
                  src={pauseButtonIcon}
                  alt="Pause"
                  className="w-4 h-4 object-contain"
                />
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewingFollowUpId(followUp.id)}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none"
              title="View Follow-up"
            >
              <img
                src={eyeIcon}
                alt="View"
                className="w-4 h-4 object-contain"
              />
            </button>
            {!readOnly && canDelete && (
              <button
                type="button"
                onClick={() =>
                  setActionPopup({ type: 'delete', id: followUp.id })
                }
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#FEEFEF] transition-colors focus:outline-none"
                title="Delete Follow-up"
              >
                <img
                  src={trashIcon}
                  alt=""
                  className="w-4 h-4 object-contain"
                />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex w-full shrink-0 flex-wrap items-center gap-3">
        <SearchInput
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search by template, message, or type"
          className="order-2 sm:order-1 w-full sm:max-w-md sm:flex-1"
          inputClassName="h-[44px]"
        />

        {!readOnly && canCreate && (
          <Button
            variant="primary"
            onClick={handleStartAddFollowUp}
            className="order-1 sm:order-2 h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto"
            icon={<PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
          >
            <span className="whitespace-nowrap">Add Follow-Up</span>
          </Button>
        )}
      </div>

      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
        <V2Table
          tight
          columns={columns}
          data={isError && !followUpsData ? [] : followUps}
          rowKey="id"
          storageKey="crm_contact_followups_column_widths"
          loading={isLoading || (!followUpsData && !isError)}
          loadingRowCount={PAGE_SIZE}
          notice={
            isError && !followUpsData
              ? parseApiError(error)?.message || 'Failed to load follow-ups'
              : null
          }
          empty="No follow-ups found for this contact."
          onRowClick={(followUp) => setViewingFollowUpId(followUp.id)}
          pagination={{
            currentPage: page,
            totalCount,
            itemsPerPage: PAGE_SIZE,
            onChange: setPage,
          }}
        />
      </div>

      <CRMFollowUpDetailModal
        companyId={companyId}
        contactId={contactId}
        followUpId={viewingFollowUpId}
        onClose={() => setViewingFollowUpId(null)}
        readOnly={readOnly || !canUpdate}
        isPausing={isPausing}
        isResuming={isResuming}
        isStopping={isStopping}
        onPause={() => setActionPopup({ type: 'pause', id: viewingFollowUpId })}
        onResume={() =>
          setActionPopup({ type: 'resume', id: viewingFollowUpId })
        }
        onStop={() => setActionPopup({ type: 'stop', id: viewingFollowUpId })}
        onReschedule={() =>
          setActionPopup({ type: 'reschedule', id: viewingFollowUpId })
        }
      />

      <StopFollowUpConfirmModal
        isOpen={actionPopup?.type === 'stop'}
        onCancel={() => {
          if (!isStopping) setActionPopup(null);
        }}
        onConfirm={() =>
          handleStopFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isStopping}
      />

      <ConfirmDeleteModal
        isOpen={actionPopup?.type === 'delete'}
        title="Delete Follow-up"
        description="This action cannot be undone and it will not execute anymore."
        onCancel={() => {
          if (!isDeleting) setActionPopup(null);
        }}
        onConfirm={() =>
          handleDeleteFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isDeleting}
      />

      <RescheduleModal
        isOpen={actionPopup?.type === 'reschedule'}
        onClose={() => {
          if (!isRescheduling) setActionPopup(null);
        }}
        onReschedule={(newDate) =>
          handleRescheduleFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
            payload: { new_date_time: newDate },
          })
        }
        isRescheduling={isRescheduling}
      />

      <ActionConfirmModal
        isOpen={actionPopup?.type === 'pause'}
        onCancel={() => {
          if (!isPausing) setActionPopup(null);
        }}
        onConfirm={() =>
          handlePauseFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isPausing}
        title="Pause Follow-up"
        description="This follow-up will pause until you resume it."
        confirmLabel="Pause"
        pendingLabel="Pausing..."
      />

      <ActionConfirmModal
        isOpen={actionPopup?.type === 'resume'}
        onCancel={() => {
          if (!isResuming) setActionPopup(null);
        }}
        onConfirm={() =>
          handleResumeFollowUp({
            companyId,
            contactId,
            followUpId: actionPopup?.id,
          })
        }
        isPending={isResuming}
        title="Resume Follow-up"
        description="This follow-up will start running again on its schedule."
        confirmLabel="Resume"
        pendingLabel="Resuming..."
      />

      <CRMFollowUpChannelModal
        isOpen={showChannelModal}
        onClose={() => setShowChannelModal(false)}
        onSelectChannel={handleSelectChannel}
        status={channels}
        canOpenSettings={can(PERMISSIONS.SETTINGS.VIEW)}
      />
    </div>
  );
}
